import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Trash2, User } from 'lucide-react';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { useBorrowerShoppingCart } from '../../contexts/main-borrower-pricing/selectors/selectors';
import { useMainBorrowerPricingActions } from '../../contexts/main-borrower-pricing/MainBorrowerPricingContext';
import { usePanelWithBorrowerPricingContext } from '../../hooks/usePanelWithBorrowerPricingContext';
import { useNavigate } from 'react-router-dom';
import { shoppingCartAPI } from 'services/swagger-api';
import { toasterService } from 'components/feedback/Toaster';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { BorrowerCartItemDto } from 'api/shopping-cart';

interface ShoppingCartContentProps {
  borrowerProjectId: string;
  onCartUpdated: (items: BorrowerCartItemDto[]) => void;
}

const ShoppingCartContent: React.FC<ShoppingCartContentProps> = ({ borrowerProjectId, onCartUpdated }) => {
  const { setPanelActions, closePanel } = usePanel();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(false);
  const [cartItems, setCartItems] = useState<BorrowerCartItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Charger le panier à l'ouverture du panel
  useEffect(() => {
    const fetchCart = async () => {
      try {
        const response = await shoppingCartAPI.fetchBorrowerShoppingCart({ borrowerProjectId });
        setCartItems(response.items ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCart();
  }, [borrowerProjectId]);

  const totalItems = cartItems.length;
  const totalPrice = useMemo(() => {
    return cartItems.reduce(
      (sum, item) => sum + (item.contractFunnel?.pricing?.clientPrice ?? 0), 0
    );
  }, [cartItems]);

  const removeContract = useCallback(async (hash?: string | null) => {
    if (!hash) return;
    try {
      const response = await shoppingCartAPI.removeBorrowerCartItem({ hash, borrowerProjectId });
      if (response.isSuccess) {
        const cart = await shoppingCartAPI.fetchBorrowerShoppingCart({ borrowerProjectId });
        const items = cart.items ?? [];
        setCartItems(items);
        // Synchroniser le context global pour que le badge du panier se mette à jour
        onCartUpdated(items);
      }
    } catch (error) {
      console.error(error);
    }
  }, [borrowerProjectId, onCartUpdated]);

  // Toujours 2 décimales : 1450.5 -> « 1 450,50 € », 1000 -> « 1 000,00 € »
  const formatPrice = (price: number) =>
    `${price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  const handleValidateCart = useCallback(async () => {
    if (!borrowerProjectId) return;

    setIsValidating(true);
    try {
      const response = await shoppingCartAPI.validatePanierEmprunteur({
        borrowerProjectId
      });

      if (response.isSuccess && response.borrowerId && response.quoteId) {
        // NB: la notification de succès (« devis créé ») est déjà affichée automatiquement
        // par l'auth-interceptor (handleNotification) pour toute réponse qui la contient.
        // On ne la ré-affiche donc PAS ici, sinon double toaster.
        // Rediriger vers la contractualisation avec les IDs retournes
        navigate(
          `/simulation/contractualisation/borrower/${borrowerProjectId}/${response.borrowerId}/${response.quoteId}`
        );
        closePanel();
      } else {
        // Afficher l'erreur
        toasterService.showToast({
          title: 'Erreur',
          message: response.errorMessage || 'Une erreur est survenue lors de la validation du panier',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red
        });
      }
    } catch (error) {
      toasterService.showToast({
        title: 'Erreur',
        message: 'Une erreur est survenue lors de la validation du panier',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
    } finally {
      setIsValidating(false);
    }
  }, [navigate, closePanel, borrowerProjectId]);

  useEffect(() => {
    setPanelActions({
      onSubmit: handleValidateCart,
      closeLabel: 'COMMON.BACK',
      submitLabel: isValidating ? 'COMMON.LABELS.LOADING' : 'COMMON.LABELS.GO_TO_CONTRACTUALIZATION',
      submitDisabled: totalItems === 0 || isValidating
    });
  }, [setPanelActions, totalItems, handleValidateCart, isValidating]);

  return (
    <div className="min-h-full">
      {totalItems === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Votre panier est vide</p>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-4 mb-6">
            {cartItems.map((item) => (
              <div key={item.name}>
                {/* Person name header */}
                <div className="flex items-center bg-primary/10 p-2 rounded-xl gap-2 mb-3">
                  <User className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold text-primary">
                    {item.name}
                  </p>
                </div>
                {/* Contracts for this person */}
                {
                  item.contractFunnel &&
                  <div
                    className="flex items-start gap-3 px-2 mb-4"
                    key={"Shopping" + item.name}
                  >
                    {/* Logo produit */}
                    {item.contractFunnel.product?.logoURL ? (
                      <img
                        src={item.contractFunnel.product.logoURL}
                        alt={item.contractFunnel.product.label}
                        className="w-10 h-10 rounded-lg object-contain bg-white border border-gray-100 p-1 flex-shrink-0 mt-0.5"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-gray-400">
                          {item.contractFunnel.product?.label?.charAt(0) ?? '?'}
                        </span>
                      </div>
                    )}
                    {/* Infos contrat */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-primary font-semibold uppercase truncate">
                        {item.contractFunnel.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.contractFunnel.product?.label}
                      </p>
                      {item.contractFunnel.product?.owner?.name && (
                        <p className="text-[11px] text-gray-400 truncate">
                          {item.contractFunnel.product.owner.name}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-400">
                        {item.contractFunnel.contractIdentifier}
                      </p>
                      <p className="text-sm font-bold mt-1.5">
                        {formatPrice(item.contractFunnel.pricing.clientPrice)}
                      </p>
                    </div>
                    {/* Bouton supprimer */}
                    <div
                      className="cursor-pointer flex-shrink-0 mt-0.5"
                      onClick={() => removeContract(item.contractFunnel.hash)}
                    >
                      <Trash2 className="w-4.5 h-4.5 text-gray-400 hover:text-red-500 transition-colors" />
                    </div>
                  </div>
                }

                {/* TODO: Détails (frais + code stat) — masqué temporairement */}
              </div>
            ))}
          </ul>
          {/* Total Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center px-2">
              <p className="text-lg font-semibold text-gray-700">Total</p>
              <p className="text-xl font-bold text-primary">
                {formatPrice(totalPrice)}
              </p>
            </div>
            <p className="text-sm text-gray-500 text-right px-2 mt-1">
              {totalItems} article{totalItems > 1 ? 's' : ''}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const ShoppingCartPanel: React.FC = () => {
  const { openPanel } = usePanelWithBorrowerPricingContext();
  const { shoppingCart, borrowerProjectId } = useBorrowerShoppingCart();
  const { updateShoppingCart } = useMainBorrowerPricingActions();

  const handleOpenCart = useCallback(() => {
    if (!borrowerProjectId) return;
    openPanel(
      {
        title: 'Votre panier',
        size: Sizes.xl
      },
      <ShoppingCartContent
        borrowerProjectId={borrowerProjectId}
        onCartUpdated={updateShoppingCart}
      />
    );
  }, [openPanel, borrowerProjectId, updateShoppingCart]);

  return (
    <button
      onClick={handleOpenCart}
      className="fixed bottom-6 right-6 bg-primary hover:bg-primary/90 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 z-30"
      aria-label="Ouvrir le panier"
    >
      <div className="relative">
        <ShoppingCart className="w-6 h-6" />
        {shoppingCart.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {shoppingCart.length}
          </span>
        )}
      </div>
    </button>
  );
};

export default ShoppingCartPanel;
